import { api } from '../contexts/AuthContext';
import { apiService } from './api/instance';
import { ProductSupplier } from './api/api.types';
import { config } from '../config/env';
import logger from '../utils/logger';

export interface PcComponent {
  id: number | string;
  name: string;
  price: number;
}

export interface UpgradeOption {
  id: number | string;
  name: string;
  price: number;
  priceDifference: number; // Difference from base component price
}

export interface CreatePcConfigRequest {
  name: string;
  description?: string;
  status?: 'draft' | 'published' | 'archived';
  condition?: 'new' | 'used';
  
  // Components
  pcCaseId?: number | string;
  pcCaseName?: string;
  pcCasePrice?: number;
  
  cpuId?: number | string;
  cpuName?: string;
  cpuPrice?: number;
  
  cpuCoolerId?: number | string;
  cpuCoolerName?: string;
  cpuCoolerPrice?: number;
  
  ramId?: number | string;
  ramName?: string;
  ramPrice?: number;
  
  storageId?: number | string;
  storageName?: string;
  storagePrice?: number;
  
  gpuId?: number | string;
  gpuName?: string;
  gpuPrice?: number;
  
  powerSupplyId?: number | string;
  powerSupplyName?: string;
  powerSupplyPrice?: number;
  
  // Metadata
  tags?: string[];
  isFeatured?: boolean;
  
  // Manual price override
  manualPrice?: number;
  
  // Upgrade options (up to 5 per component)
  pcCaseUpgrades?: PcComponent[];
  cpuUpgrades?: PcComponent[];
  cpuCoolerUpgrades?: PcComponent[];
  ramUpgrades?: PcComponent[];
  storageUpgrades?: PcComponent[];
  gpuUpgrades?: PcComponent[];
  powerSupplyUpgrades?: PcComponent[];
}

export interface PcConfiguration {
  id: number;
  name: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  condition?: 'new' | 'used';
  
  // Components with full details
  pcCaseId?: number | string;
  pcCaseSupplier?: ProductSupplier | null;
  pcCaseName?: string;
  pcCasePrice?: number;
  
  cpuId?: number | string;
  cpuSupplier?: ProductSupplier | null;
  cpuName?: string;
  cpuPrice?: number;
  
  cpuCoolerId?: number | string;
  cpuCoolerSupplier?: ProductSupplier | null;
  cpuCoolerName?: string;
  cpuCoolerPrice?: number;
  
  motherboardId?: number | string;
  motherboardSupplier?: ProductSupplier | null;
  motherboardName?: string;
  motherboardPrice?: number;
  
  ramId?: number | string;
  ramSupplier?: ProductSupplier | null;
  ramName?: string;
  ramPrice?: number;
  
  ssdId?: number | string;
  ssdSupplier?: ProductSupplier | null;
  ssdName?: string;
  ssdPrice?: number;
  
  hddId?: number | string;
  hddSupplier?: ProductSupplier | null;
  hddName?: string;
  hddPrice?: number;
  
  storageId?: number | string;
  storageName?: string;
  storagePrice?: number;
  
  gpuId?: number | string;
  gpuSupplier?: ProductSupplier | null;
  gpuName?: string;
  gpuPrice?: number;
  
  powerSupplyId?: number | string;
  powerSupplySupplier?: ProductSupplier | null;
  powerSupplyName?: string;
  powerSupplyPrice?: number;
  
  totalPrice: number;
  manualPrice?: number;
  tags: string[];
  isFeatured: boolean;
  viewCount: number;
  imageUrl?: string; // Legacy single image
  imageUrls?: string[]; // New multiple images array
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  
  // Upgrade options
  pcCaseUpgrades?: PcComponent[];
  cpuUpgrades?: PcComponent[];
  motherboardUpgrades?: PcComponent[];
  cpuCoolerUpgrades?: PcComponent[];
  ramUpgrades?: PcComponent[];
  ssdUpgrades?: PcComponent[];
  hddUpgrades?: PcComponent[];
  storageUpgrades?: PcComponent[];
  gpuUpgrades?: PcComponent[];
  powerSupplyUpgrades?: PcComponent[];
}

export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'draft' | 'published' | 'archived';
  condition?: 'new' | 'used';
  isFeatured?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  minPrice?: number;
  maxPrice?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export class PcConfigService {
  // Product caching for performance optimization
  private static productCache = new Map<string, { data: any[], timestamp: number }>();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  // Public endpoints - no authentication required
  static async getPublishedConfigs(params?: QueryParams): Promise<PaginatedResponse<PcConfiguration>> {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
      }
      
      const response = await fetch(`${config.api.baseUrl}/pc-configs/published?${searchParams.toString()}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch published configurations');
      }

      return response.json();
    } catch (error) {
      logger.error('Error fetching published configurations:', error);
      throw error;
    }
  }

  static async getPublishedConfig(id: number): Promise<PcConfiguration> {
    try {
      const response = await fetch(`${config.api.baseUrl}/pc-configs/published/${id}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Configuration not found');
      }

      return response.json();
    } catch (error) {
      logger.error('Error fetching published configuration:', error);
      throw error;
    }
  }

  // Admin endpoints - require authentication
  static async getAllConfigs(params?: QueryParams): Promise<PaginatedResponse<PcConfiguration>> {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
      }
      
      const response = await api.get(`/admin/pc-configs?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching configurations:', error);
      throw error;
    }
  }

  static async getConfig(id: number): Promise<PcConfiguration> {
    try {
      const response = await api.get(`/pc-configs/${id}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching configuration:', error);
      throw error;
    }
  }

  static async createConfig(data: CreatePcConfigRequest): Promise<PcConfiguration> {
    try {
      logger.log('Creating PC configuration:', data);
      const response = await api.post('/pc-configs', data);
      return response.data;
    } catch (error: any) {
      logger.error('Error creating configuration:', error);
      if (error.response) {
        logger.error('Response status:', error.response.status);
        logger.error('Response data:', error.response.data);
      }
      throw error;
    }
  }

  static async updateConfig(id: number, data: Partial<CreatePcConfigRequest>): Promise<PcConfiguration> {
    try {
      logger.log('Updating PC configuration:', id, data);
      const response = await api.patch(`/pc-configs/${id}`, data);
      return response.data;
    } catch (error: any) {
      logger.error('Error updating configuration:', error);
      if (error.response) {
        logger.error('Response status:', error.response.status);
        logger.error('Response data:', error.response.data);
      }
      throw error;
    }
  }

  static async deleteConfig(id: number): Promise<void> {
    try {
      // Use admin endpoint for proper deletion including images
      await api.delete(`/admin/pc-configs/${id}`);
    } catch (error) {
      logger.error('Error deleting configuration:', error);
      throw error;
    }
  }

  // Admin only methods
  static async updateStatus(id: number, status: 'draft' | 'published' | 'archived'): Promise<PcConfiguration> {
    try {
      const response = await api.patch(`/admin/pc-configs/${id}/status`, { status });
      return response.data;
    } catch (error) {
      logger.error('Error updating configuration status:', error);
      throw error;
    }
  }

  static async setFeatured(id: number, isFeatured: boolean): Promise<PcConfiguration> {
    try {
      const response = await api.patch(`/admin/pc-configs/${id}/featured`, { isFeatured });
      return response.data;
    } catch (error) {
      logger.error('Error updating featured status:', error);
      throw error;
    }
  }

  // Product search for components - use vali API directly to avoid authentication issues
  static async searchProducts(categoryId: number, searchTerm?: string): Promise<any[]> {
    try {
      logger.log(`Searching products for category ${categoryId}, searchTerm: ${searchTerm}`);
      
      const params = {
        page: 1,
        per_page: 50,
        ...(searchTerm && { search: searchTerm })
      };
      
      const response = await apiService.getProductsByCategoryFull(categoryId, params);
      logger.log(`Search results for category ${categoryId}:`, response);
      
      return response.items || [];
    } catch (error) {
      logger.error(`Error searching products for category ${categoryId}:`, error);
      return [];
    }
  }

  // Cache management methods
  private static getCacheKey(categoryId: number, limit?: number): string {
    return `products_${categoryId}_${limit || 'all'}`;
  }

  private static isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  private static getCachedProducts(categoryId: number, limit?: number): any[] | null {
    const cacheKey = this.getCacheKey(categoryId, limit);
    const cached = this.productCache.get(cacheKey);
    
    if (cached && this.isCacheValid(cached.timestamp)) {
      logger.log(`Using cached products for category ${categoryId} (${cached.data.length} items)`);
      return cached.data;
    }
    
    return null;
  }

  private static setCachedProducts(categoryId: number, products: any[], limit?: number): void {
    const cacheKey = this.getCacheKey(categoryId, limit);
    this.productCache.set(cacheKey, {
      data: products,
      timestamp: Date.now()
    });
    logger.log(`Cached ${products.length} products for category ${categoryId}`);
  }

  // Helper method to get products by category - optimized with caching
  static async getProductsByCategory(categoryId: number, limit?: number): Promise<any[]> {
    try {
      // Check cache first
      const cachedProducts = this.getCachedProducts(categoryId, limit);
      if (cachedProducts) {
        return cachedProducts;
      }

      logger.log(`Fetching products for category ${categoryId}${limit ? `, limit: ${limit}` : ', no limit'}`);
      
      // The endpoint returns the whole category in one response and only pages
      // it client-side, so asking for page 2, 3... would re-download it each time.
      const response = await apiService.getProductsByCategoryFull(categoryId, {
        page: 1,
        per_page: limit ?? Number.MAX_SAFE_INTEGER
      });
      const allItems = response.items || [];

      // Cache the results
      this.setCachedProducts(categoryId, allItems, limit);
      
      return allItems;
    } catch (error) {
      logger.error(`Error fetching products for category ${categoryId}:`, error);
      return [];
    }
  }

  // Method to clear cache (useful for admin operations)
  static clearProductCache(): void {
    this.productCache.clear();
    logger.log('Product cache cleared');
  }

  // Older configurations were saved before the supplier was stored, and back
  // then every case came from Vali.
  static async getPcCaseImageUrl(pcCaseId: number | string, supplier?: ProductSupplier | null): Promise<string | null> {
    const images = await this.getPcCaseImages(pcCaseId, supplier);
    return images[0] ?? null;
  }

  static async getPcCaseImages(pcCaseId: number | string, supplier?: ProductSupplier | null): Promise<string[]> {
    const caseSupplier = supplier ?? 'vali';
    try {
      const product = await apiService.getProductBySupplierFull(caseSupplier, String(pcCaseId));
      return product?.images?.map((img: any) => img.href) ?? [];
    } catch (error) {
      logger.error(`Error fetching PC case images for ${caseSupplier} product ${pcCaseId}:`, error);
      return [];
    }
  }

  // Upload custom image for PC configuration (legacy single image)
  static async uploadConfigImage(configId: number, file: File): Promise<PcConfiguration> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post(`/admin/pc-configs/${configId}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      logger.log('Image uploaded successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error uploading configuration image:', error);
      throw error;
    }
  }

  // Add images to existing configuration (append, not replace)
  static async addConfigImages(configId: number, files: File[]): Promise<PcConfiguration> {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('images', file);
      });

      const response = await api.post(`/admin/pc-configs/${configId}/add-images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      logger.log('Images added successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error adding configuration images:', error);
      throw error;
    }
  }

  // Remove custom image from PC configuration (legacy)
  static async removeConfigImage(configId: number): Promise<PcConfiguration> {
    try {
      const response = await api.delete(`/admin/pc-configs/${configId}/image`);
      logger.log('Image removed successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error removing configuration image:', error);
      throw error;
    }
  }

  // Remove all images from PC configuration
  static async removeAllConfigImages(configId: number): Promise<PcConfiguration> {
    try {
      const response = await api.delete(`/admin/pc-configs/${configId}/images`);
      logger.log('All images removed successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error removing all configuration images:', error);
      throw error;
    }
  }

  // Remove specific image by index
  static async removeConfigImageByIndex(configId: number, imageIndex: number): Promise<PcConfiguration> {
    try {
      const response = await api.delete(`/admin/pc-configs/${configId}/image/${imageIndex}`);
      logger.log('Image removed successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error removing configuration image by index:', error);
      throw error;
    }
  }
}